import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader/root';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box } from '@rebass/grid';
import { DynamicThemeProvider, ZINDEX, MatchMedia, GreyPalette } from '@amtrav/themes';
import { AutocompleteRenderer, FullPageOverlay, Autocomplete } from '@amtrav/view-components';
import { useHotkeys } from 'react-hotkeys-hook';
import { genericSearch } from './webservice/genericSearch';
import { useDebounce } from '../../hooks';
import JumpToInput from './components/JumpToInput';
import Hint from './components/Hint';
import { groupedMap } from './helpers';
import siteNavData from './sitenavData';

const JUMP_TO_TYPE = {
  profile: 'Profile',
  knowledgeBase: 'KnowledgeBase',
  siteNavigation: 'SiteNav',
  booking: 'Booking',
};

const Dropdown = styled(Autocomplete.Dropdown)`
  position: unset;
`;

const Wrapper = styled.div`
  position: relative;
  height: 40px;
  z-index: ${({ withFocus }) => (withFocus ? ZINDEX.POPOVER : 0)};
  width: 200px;
  box-shadow: ${({ withFocus }) =>
    withFocus ? '0 4px 20px 4px rgba(0, 20, 60, 0.1), 0 4px 80px -8px rgba(0, 20, 60, 0.2);' : 'none'};
`;

const SearchWrapper = styled.div`
  position: absolute;
  width: ${({ withFocus }) => (withFocus ? '520px' : '200px')};
  z-index: ${ZINDEX.POPOVER};
  transition: width 0.2s;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
`;

const NoResults = styled(Box)`
  font-weight: bold;
  background: ${GreyPalette['200']};
  font-size: 15px;
`;

const JumpTo = ({ themeName, getLogonProfile }) => {
  const [results, setResults] = useState([]);
  const [withFocus, setWithFocus] = useState(false);
  const [userData, updateUserData] = useState(null);
  const [navData, updateNavData] = useState([]);
  const [isWaiting, setIsWaiting] = useState(false);

  const setIsWaitingWithTimeout = (isWait) => {
    setTimeout(() => {
      setIsWaiting(isWait);
    }, 500);
  };

  const setWithFocusOn = () => {
    setWithFocus(true);
  };
  const setWithFocusOff = () => {
    setWithFocus(false);
  };

  useEffect(() => {
    getLogonProfile()
      .then((data) => {
        updateUserData(data);
        // navigation items are filtered so only applicable ones are shown to the user (isVisible flag)
        updateNavData(siteNavData(data?.viewSettings, data?.accountLevel).filter((item) => item?.metadata?.isVisible));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  }, [getLogonProfile]);

  const InputEl = useRef();

  useHotkeys(
    'ctrl+k, command+k',
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      InputEl.current.focus();
    },
    {
      filterPreventDefault: false,
    },
  );

  const onChange = (t) => {
    setWithFocusOff();
    if (t?.url) {
      const sanitizedURL = t.url.replace(/'/g, '');

      if (t?.type === JUMP_TO_TYPE.knowledgeBase || t?.type === JUMP_TO_TYPE.booking || t?.metadata?.newWindow) {
        window.open(sanitizedURL, '_blank');
      } else {
        document.location.href = sanitizedURL;
      }
    }
  };

  const filterProfiles = (data) => {
    // Only return profiles that a user has permission to manage (i.e. are in the bookForList)
    return data.filter((jumpToItem) =>
      jumpToItem.type === JUMP_TO_TYPE.profile
        ? userData.bookForList.some((bookPerson) => bookPerson.nameNum === jumpToItem.metadata.nameNum)
        : true,
    );
  };

  const inputChangeHandler = (val) => {
    if (val) {
      genericSearch(val)
        .then((res) => {
          setIsWaitingWithTimeout(false);
          // navData need to be filtered with the input
          const filteredItems = navData
            .filter((item) => {
              return val.length >= 2 && item?.metadata?.keywords.toLocaleLowerCase().includes(val.toLocaleLowerCase());
            })
            .concat(filterProfiles(res?.results));

          setResults(filteredItems || []);
        })
        .catch((err) => {
          setIsWaitingWithTimeout(false);
          // eslint-disable-next-line no-console
          console.error(err);
        });
    } else {
      setResults([]);
    }
  };

  const [inputText, setInputText] = useDebounce(
    '',
    (debouncedText) => {
      inputChangeHandler(debouncedText);
    },
    220,
  );

  /* We only want to send the first argument to setInputText(), otherwise React will
    throw an error "Hooks don't support the second callback argument." This occurs because
    Downshift will blindly send all arguments to the callback from onInputValueChange. The 
    solution is to wrap the call in another function only sending the first argument. */
  const onInputValueChange = (txt) => {
    setIsWaiting(true); // webservice call is made on each input value change
    return setInputText(txt);
  };

  const itemToString = () => '';

  const MAX_DISPLAYED_OPTIONS = 10;

  const regexHtmlTags = /(<([^>]+)>)/gi;

  // prevent JumpTo navigation from rendering if userData is empty
  if (!userData) return null;

  return (
    <DynamicThemeProvider themeName={themeName}>
      <MatchMedia.Medium>
        <Wrapper withFocus={withFocus}>
          <AutocompleteRenderer
            labelKey="title"
            items={results}
            onInputValueChange={onInputValueChange}
            itemToString={itemToString}
            onChange={onChange}
            defaultHighlightedIndex={0}
            defaultSelectedItem={null}>
            {(dsProps, ddProps) => {
              const { getInputProps, getItemProps, isOpen, highlightedIndex } = dsProps;
              const { selectedItem: ddSelectedItem } = ddProps;

              const resolvedSelectedItem = ddSelectedItem || undefined;

              // groupedMap will give us a JavaScript set to group the results by 'type'
              const mappedResults = groupedMap(results.slice(0, MAX_DISPLAYED_OPTIONS), 'type');

              const numberOfSiteNavigation = mappedResults.get(JUMP_TO_TYPE.siteNavigation)?.length || 0;
              const numberOfResultsProfiles = mappedResults.get(JUMP_TO_TYPE.profile)?.length || 0;
              const numberOfKnowledgeBase = mappedResults.get(JUMP_TO_TYPE.knowledgeBase)?.length || 0;

              const matchedNav = mappedResults.has(JUMP_TO_TYPE.siteNavigation) ? (
                <>
                  <Autocomplete.SectionTitle>Site Navigation</Autocomplete.SectionTitle>
                  <Autocomplete.Options>
                    {mappedResults.get(JUMP_TO_TYPE.siteNavigation).map((item, index) => {
                      const highlighted = highlightedIndex === index;

                      return (
                        <Autocomplete.Option
                          {...getItemProps({
                            key: item.title,
                            index,
                            item,
                            isActive: highlighted,
                            isSelected: resolvedSelectedItem?.title === item.title,
                          })}
                          subtitle={item?.metadata?.category}>
                          {item.title}
                        </Autocomplete.Option>
                      );
                    })}
                  </Autocomplete.Options>
                </>
              ) : null;

              const matchedProfiles = mappedResults.has(JUMP_TO_TYPE.profile) ? (
                <>
                  <Autocomplete.SectionTitle>People</Autocomplete.SectionTitle>
                  <Autocomplete.Options>
                    {mappedResults.get(JUMP_TO_TYPE.profile).map((item, index) => {
                      // index needs to be offset by previous results
                      const adjustedIndex = index + numberOfSiteNavigation;
                      const highlighted = highlightedIndex === adjustedIndex;

                      return (
                        <Autocomplete.OptionPerson
                          {...getItemProps({
                            key: item.title,
                            index: adjustedIndex,
                            item,
                            isActive: highlighted,
                            isSelected: resolvedSelectedItem?.title === item.title,
                          })}
                          initials={item?.metadata?.initials}
                          imgUrl={item?.metadata?.profileImageUrl}>
                          {item?.title}
                        </Autocomplete.OptionPerson>
                      );
                    })}
                  </Autocomplete.Options>
                </>
              ) : null;

              const matchedKnowledgeBase = mappedResults.has(JUMP_TO_TYPE.knowledgeBase) ? (
                <>
                  <Autocomplete.SectionTitle>Knowledge Base</Autocomplete.SectionTitle>
                  <Autocomplete.Options>
                    {mappedResults.get(JUMP_TO_TYPE.knowledgeBase).map((item, index) => {
                      // index needs to be offset by previous results
                      const adjustedIndex = index + numberOfSiteNavigation + numberOfResultsProfiles;
                      const highlighted = highlightedIndex === adjustedIndex;

                      return (
                        <Autocomplete.Option
                          {...getItemProps({
                            key: item.title,
                            index: adjustedIndex,
                            item,
                            isActive: highlighted,
                            isSelected: resolvedSelectedItem?.title === item.title,
                          })}
                          subtitle={item?.metadata?.category}>
                          {item.title.replace(regexHtmlTags, '')}
                        </Autocomplete.Option>
                      );
                    })}
                  </Autocomplete.Options>
                </>
              ) : null;

              const matchedBookings = mappedResults.has(JUMP_TO_TYPE.booking) ? (
                <>
                  <Autocomplete.SectionTitle>Bookings</Autocomplete.SectionTitle>
                  <Autocomplete.Options>
                    {mappedResults.get(JUMP_TO_TYPE.booking).map((item, index) => {
                      // index needs to be offset by previous results
                      const adjustedIndex =
                        index + numberOfSiteNavigation + numberOfResultsProfiles + numberOfKnowledgeBase;
                      const highlighted = highlightedIndex === adjustedIndex;

                      return (
                        <Autocomplete.Option
                          {...getItemProps({
                            key: item.description,
                            index: adjustedIndex,
                            item,
                            isActive: highlighted,
                            isSelected: resolvedSelectedItem?.title === item.title,
                          })}
                          subtitle={item?.metadata?.lastName}>
                          {item.description.replace(regexHtmlTags, '')}
                        </Autocomplete.Option>
                      );
                    })}
                  </Autocomplete.Options>
                </>
              ) : null;

              return (
                <>
                  {withFocus ? <FullPageOverlay /> : null}
                  <SearchWrapper withFocus={withFocus}>
                    <JumpToInput
                      {...getInputProps({
                        id: 'quickSearch',
                        placeholder: withFocus ? '' : 'Quick search',
                        onKeyDown: ({ key, nativeEvent }) => {
                          if (key === 'Home' || key === 'End') {
                            // eslint-disable-next-line no-param-reassign
                            nativeEvent.preventDownshiftDefault = true;
                          }
                          if (key === 'Escape') {
                            // eslint-disable-next-line no-param-reassign
                            nativeEvent?.target?.blur();
                          }
                        },
                        autoComplete: 'off',
                        value: inputText,
                        onFocus: setWithFocusOn,
                        onBlur: setWithFocusOff,
                        withFocus,
                        isOpen,
                      })}
                      ref={InputEl}
                    />
                    {isOpen && results.length && withFocus ? (
                      <Dropdown>
                        {matchedNav}
                        {matchedProfiles}
                        {matchedKnowledgeBase}
                        {matchedBookings}
                      </Dropdown>
                    ) : null}
                    {results.length === 0 && withFocus ? (
                      <Dropdown>
                        {inputText.length >= 4 && !isWaiting ? (
                          <NoResults p={3}>No results matched your search</NoResults>
                        ) : null}
                        <Hint hasText={inputText.length > 0} />
                      </Dropdown>
                    ) : null}
                  </SearchWrapper>
                </>
              );
            }}
          </AutocompleteRenderer>
        </Wrapper>
      </MatchMedia.Medium>
    </DynamicThemeProvider>
  );
};

JumpTo.propTypes = {
  themeName: PropTypes.string.isRequired,
  getLogonProfile: PropTypes.func.isRequired,
};

export default process.env.USE_HMR ? hot(JumpTo) : JumpTo;
